import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { PageShell } from "@/components/PageShell";
import { Section } from "@/components/Section";
import { LoadingState, ErrorState } from "@/components/LoadingState";
import { StoreFilterBar } from "@/components/StoreFilterBar";
import {
  HoursTimeline,
  TimelineLegend,
  type AxisZone,
} from "@/components/HoursTimeline";
import { useStoresWithMeta, retailerNames } from "@/lib/queries";
import {
  allTimeZones,
  isValidTimeZone,
  offsetLabel,
  viewerTimeZone,
  weekdayIn,
} from "@/lib/timezone";
import { dayLabel } from "@/lib/operatingHours";
import { filterStores, type StoreStatus } from "@/lib/storeSearch";

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

/** Which clock the axis is drawn on. `other` reads from the zone dropdown. */
type AxisChoice = "store" | "viewer" | "other";

/** The now-line only needs to be minute-accurate. */
const TICK_MS = 30_000;

export function CalendarPage() {
  const query = useStoresWithMeta();
  const viewerZone = useMemo(viewerTimeZone, []);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const today = weekdayIn(viewerZone, now) ?? "monday";
  const [day, setDay] = useState(today);
  const [choice, setChoice] = useState<AxisChoice>("store");
  const [otherZone, setOtherZone] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [timezone, setTimezone] = useState<string | null>(null);
  const [retailers, setRetailers] = useState<string[]>([]);
  const [status, setStatus] = useState<StoreStatus | null>(null);

  const retailerOptions = useMemo(
    () => retailerNames(query.data?.stores ?? []),
    [query.data],
  );

  // Without inventory every `active` is null, so the control would
  // silently empty the table whichever way it was set.
  const statusKnown = useMemo(
    () => (query.data?.stores ?? []).some((store) => store.active !== null),
    [query.data],
  );

  /** Zones the fleet actually uses — the most likely third choices. */
  const storeZones = useMemo(() => {
    const set = new Set<string>();
    for (const store of query.data?.stores ?? []) set.add(store.timezone);
    return [...set].filter(isValidTimeZone).sort();
  }, [query.data]);

  // UTC offsets only move at DST boundaries, so rebuild the ~400-entry zone
  // list at most once an hour rather than on every 30s tick of `now` — which
  // otherwise churned a new `data` array under an open dropdown.
  const offsetHour = Math.floor(now.getTime() / 3_600_000);

  const zoneOptions = useMemo(() => {
    const at = new Date(offsetHour * 3_600_000);
    const label = (zone: string) => {
      const offset = offsetLabel(zone, at);
      return { value: zone, label: offset ? `${zone} · ${offset}` : zone };
    };
    const inUse = new Set(storeZones);
    const rest = allTimeZones().filter((zone) => !inUse.has(zone));

    const groups = [];
    if (storeZones.length) {
      groups.push({ group: "Used by stores", items: storeZones.map(label) });
    }
    if (rest.length) {
      groups.push({ group: "All timezones", items: rest.map(label) });
    }
    // A runtime without Intl.supportedValuesOf still gets the store zones.
    return groups;
  }, [storeZones, offsetHour]);

  // "Other…" falls back to the viewer's zone until one is actually picked,
  // so the grid always has an axis to draw against.
  let axisZone: AxisZone;
  if (choice === "store") {
    axisZone = null;
  } else if (choice === "viewer") {
    axisZone = viewerZone;
  } else {
    axisZone = otherZone ?? viewerZone;
  }

  const stores = useMemo(
    () =>
      filterStores(query.data?.stores ?? [], {
        search,
        timezone,
        retailers,
        status,
      }),
    [query.data, search, timezone, retailers, status],
  );

  const axisWeekday = axisZone ? weekdayIn(axisZone, now) : null;
  // The now-line is only meaningful when the grid is showing the axis's today.
  const showsNowLine = axisZone ? axisWeekday === day : day === today;

  return (
    <PageShell
      title="Opening hours"
      subtitle={
        axisZone
          ? `All rows on one clock (${axisZone}) — bars stagger by offset, so the red line reads across the fleet.`
          : "Each row on that store's own clock — hours line up, so “now” is marked per store."
      }
    >
      <StoreFilterBar
        search={search}
        onSearchChange={setSearch}
        retailerOptions={retailerOptions}
        retailers={retailers}
        onRetailersChange={setRetailers}
        status={status}
        onStatusChange={setStatus}
        statusKnown={statusKnown}
        timezoneOptions={storeZones}
        timezone={timezone}
        onTimezoneChange={setTimezone}
      />
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap" gap="sm">
          <SegmentedControl
            size="xs"
            value={day}
            onChange={setDay}
            data={DAYS.map((value) => ({
              value,
              label:
                value === today
                  ? `${dayLabel(value).slice(0, 3)} •`
                  : dayLabel(value).slice(0, 3),
            }))}
          />

          <Group gap="xs" wrap="wrap">
            <SegmentedControl
              size="xs"
              value={choice}
              onChange={(value) => setChoice(value as AxisChoice)}
              data={[
                { value: "store", label: "Store-local" },
                { value: "viewer", label: "Your timezone" },
                { value: "other", label: "Other…" },
              ]}
            />
            <Select
              placeholder="Pick a timezone"
              data={zoneOptions}
              value={otherZone}
              onChange={(value) => {
                setOtherZone(value);
                // Picking a zone is the intent to use it.
                if (value) setChoice("other");
              }}
              searchable
              clearable
              nothingFoundMessage="No matching timezone"
              // Kept mounted so the choice survives toggling back and forth.
              disabled={choice !== "other"}
              style={{ flex: "1 1 240px" }}
              miw={200}
              comboboxProps={{ withinPortal: true }}
            />
          </Group>
        </Group>

        {query.isLoading ? <LoadingState label="Loading stores…" /> : null}
        {query.error ? <ErrorState error={query.error} /> : null}

        {query.data ? (
          <Section
            title={`${dayLabel(day)}${day === today ? " (today)" : ""}`}
            subtitle={
              <>
                {stores.length} store{stores.length === 1 ? "" : "s"}.{" "}
                {showsNowLine
                  ? "The red line marks the current time."
                  : `No current-time line — it is not ${dayLabel(day)} in ${
                      axisZone ?? "the store's timezone"
                    }.`}
              </>
            }
          >
            <Stack gap="sm">
              <TimelineLegend axisZone={axisZone} />
              {stores.length === 0 ? (
                <Text c="dimmed" size="sm">
                  No stores match those filters.
                </Text>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <HoursTimeline
                    stores={stores}
                    day={day}
                    axisZone={axisZone}
                    now={now}
                  />
                </div>
              )}
            </Stack>
          </Section>
        ) : null}

        {axisZone ? (
          <Alert color="blue" variant="light" title="Reading a shared axis">
            <Text size="sm">
              Bars show each store&apos;s {dayLabel(day)} hours redrawn on the{" "}
              {axisZone} clock ({offsetLabel(axisZone, now)}). A store far
              enough east or west has its window wrapped around midnight, so it
              can appear as two bars.
            </Text>
          </Alert>
        ) : null}
      </Stack>
    </PageShell>
  );
}
